import { cn } from "@/lib/utils";
import { computePosition, flip, shift, size } from "@floating-ui/react";
import Mention from "@tiptap/extension-mention";
import { Editor, posToDOMRect, ReactRenderer } from "@tiptap/react";
import { CommandPanel, CommandPanelRef } from "./CommandPanel";

/**
 * True when inside of a shadcn Dialog box.
 */
const isInsideDialog = (editor: Editor): boolean => {
  const editorElement = editor.view.dom;
  return editorElement.closest('[data-slot="dialog-content"]') !== null;
};

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      ),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [
      shift(),
      flip(),
      size({
        apply({ availableHeight, elements }) {
          const maxHeight = Math.max(100, Math.min(availableHeight, 400));
          elements.floating.style.maxHeight = `${maxHeight}px`;
        },
      }),
    ],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = y + element.getBoundingClientRect().height + "px";
    element.style.transform = "translateY(-100%)";

    element.className = cn(
      "border border-foreground-3 overflow-auto bg-background rounded-md shadow-lg",
      isInsideDialog(editor) ? "z-[60] pointer-events-auto" : "",
    );

    // Shadcn Dialog captures wheel; stopPropagation so the command palette scrolls.
    if (isInsideDialog(editor)) {
      element.addEventListener(
        "wheel",
        (e) => {
          e.stopPropagation();
        },
        { passive: false },
      );
    }
  });
};

type GetCommandOptions = {
  openChange?: (open: boolean) => void;
  includeSkillMentions?: boolean;
  excludeSkillUuid?: string;
};

/** Soft-delete tooltip/class for knowledge file mentions in the composer. */
const CommandMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      unavailable: {
        default: null,
        parseHTML: (element) =>
          element.classList.contains("mention-unavailable")
            ? (element.getAttribute("title") ?? true)
            : null,
        renderHTML: (attributes) => {
          if (!attributes.unavailable) {
            return {};
          }
          return {
            class: "mention-unavailable",
            title:
              typeof attributes.unavailable === "string"
                ? attributes.unavailable
                : undefined,
          };
        },
      },
    };
  },
});

const getCommandExtension = ({
  openChange,
  includeSkillMentions,
  excludeSkillUuid,
}: GetCommandOptions = {}) => {
  return CommandMention.configure({
    HTMLAttributes: {
      class: `mention`,
    },
    renderText: ({ node }) => {
      return node.attrs.id;
    },
    suggestion: {
      items: () => [], // Command component loads its own data
      render: () => {
        let component: ReactRenderer<CommandPanelRef> | undefined;

        const getComponentElement = () =>
          component?.element as HTMLDivElement | undefined;

        const destroyComponent = () => {
          component?.destroy();
          component = undefined;
          openChange?.(false);
        };

        return {
          onStart: (props) => {
            // @ts-ignore React type compatibility varies between local and CI environments
            component = new ReactRenderer(CommandPanel, {
              props: { ...props, includeSkillMentions, excludeSkillUuid },
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            const element = getComponentElement();
            if (!element) {
              return;
            }

            element.style.position = "absolute";
            document.body.appendChild(element);
            updatePosition(props.editor, element);
            openChange?.(true);
          },

          onUpdate(props) {
            if (!component) {
              return;
            }

            component.updateProps({
              ...props,
              includeSkillMentions,
              excludeSkillUuid,
            });

            const element = getComponentElement();
            if (!props.clientRect || !element) {
              return;
            }

            updatePosition(props.editor, element);
          },

          onKeyDown(props) {
            if (props.event.key === "Escape") {
              destroyComponent();
              return true;
            }

            return component?.ref?.onKeyDown(props.event) ?? false;
          },

          onExit() {
            destroyComponent();
          },
        };
      },
      char: "@",
      allowSpaces: true,
      startOfLine: false,
    },
  });
};

export class Command {
  static configure(options: GetCommandOptions = {}) {
    return getCommandExtension(options);
  }
}
